import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCurrentUserPermissions } from '../services/permissionAPI';

interface PermissionContextType {
  permissions: string[];
  roles: Array<{
    id: number;
    name: string;
    company: string;
  }>;
  isLoading: boolean;
  isSuperuser: boolean;
  currentCompany: {
    id: number;
    name: string;
  } | null;
  hasPermission: (permissionCode: string) => boolean;
  hasAnyPermission: (permissionCodes: string[]) => boolean;
  hasAllPermissions: (permissionCodes: string[]) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<Array<{
    id: number;
    name: string;
    company: string;
  }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [currentCompany, setCurrentCompany] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const loadPermissions = async () => {
    try {
      setIsLoading(true);
      
      const response = await getCurrentUserPermissions();
      
      if (response.success) {
        setPermissions(response.permission_codes);
        setRoles(response.roles);
        setIsSuperuser(response.is_superuser);
        setCurrentCompany(response.current_company);
      }
    } catch (error) {
      // İstifadəçi daxil olmayıbsa, icazələri təmizlə
      setPermissions([]);
      setRoles([]);
      setIsSuperuser(false);
      setCurrentCompany(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Yalnız localStorage-da token varsa icazələri yüklə
    const token = localStorage.getItem('access_token');
    if (token) {
      loadPermissions();
    } else {
      setIsLoading(false);
    }
  }, []);

  const hasPermission = (permissionCode: string): boolean => {
    // Əgər user superuser-dirsə, bütün icazələri ver
    if (isSuperuser) {
      return true;
    }
    
    return permissions.includes(permissionCode);
  };

  const hasAnyPermission = (permissionCodes: string[]): boolean => {
    if (isSuperuser) return true;
    return permissionCodes.some(code => permissions.includes(code));
  };

  const hasAllPermissions = (permissionCodes: string[]): boolean => {
    if (isSuperuser) return true;
    return permissionCodes.every(code => permissions.includes(code));
  };

  const refreshPermissions = async () => {
    await loadPermissions();
  };

  const value: PermissionContextType = {
    permissions,
    roles,
    isLoading,
    isSuperuser,
    currentCompany,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// Permission yoxlama komponenti
interface PermissionGuardProps {
  permission?: string;
  anyPermissions?: string[];
  allPermissions?: string[];
  fallback?: ReactNode;
  children: ReactNode;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  anyPermissions,
  allPermissions,
  fallback = null,
  children
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } = usePermissions();

  if (isLoading) {
    return <div className="animate-pulse">Yüklənir...</div>;
  }

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (anyPermissions) {
    hasAccess = hasAnyPermission(anyPermissions);
  } else if (allPermissions) {
    hasAccess = hasAllPermissions(allPermissions);
  } else {
    hasAccess = true; // Heç bir icazə tələb olunmursa
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};